import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  ArrowLeft, Users, PenTool, MessageSquare, Send, CalendarCheck,
  Trophy, Search, Eye, TrendingUp, Clock, Flame, Star, Rocket,
  User as UserIcon, Download, FileText, Mail, Plus, Trash2, UserPlus, Key, KeyRound, Copy, Check, Link
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_saleshustler/artifacts/lbev3bte_Gemini_Generated_Image_r450fmr450fmr450.png";

const CATEGORIES = [
  { id: "posts", label: "Posts", icon: PenTool, target: 20 },
  { id: "comments", label: "Comments", icon: MessageSquare, target: 20 },
  { id: "connections", label: "Connections", icon: Users, target: 20 },
  { id: "messages", label: "Messages", icon: Send, target: 20 },
  { id: "meetings", label: "Meetings", icon: CalendarCheck, target: 5 },
];

const BADGE_ICONS = {
  rocket: Rocket,
  trophy: Trophy,
  calendar: CalendarCheck,
  flame: Flame,
  star: Star,
  pen: PenTool,
  message: MessageSquare,
  users: Users,
  send: Send,
};

export default function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [weekStart, setWeekStart] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Email whitelist state
  const [approvedEmails, setApprovedEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [showEmailManager, setShowEmailManager] = useState(false);
  
  // Password reset state
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  
  // Login code state
  const [showCodeGenerator, setShowCodeGenerator] = useState(false);
  const [codeUser, setCodeUser] = useState(null);
  const [generatedCode, setGeneratedCode] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeEmail, setCodeEmail] = useState("");
  
  // Direct access link state
  const [showDirectLink, setShowDirectLink] = useState(false);
  const [directLink, setDirectLink] = useState(null);
  const [directLinkLoading, setDirectLinkLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchApprovedEmails();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, {
        credentials: 'include'
      });
      
      if (response.status === 403) {
        navigate('/dashboard');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setWeekStart(data.week_start);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    setDetailsLoading(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserDetails(data);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Email whitelist functions
  const fetchApprovedEmails = async () => {
    try {
      const response = await fetch(`${API}/admin/approved-emails`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setApprovedEmails(data.approved_emails || []);
      }
    } catch (error) {
      console.error('Error fetching approved emails:', error);
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    
    setEmailsLoading(true);
    try {
      const response = await fetch(`${API}/admin/add-approved-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: newEmail.trim().toLowerCase() })
      });
      
      if (response.ok) {
        toast.success(`Added ${newEmail} to approved list`);
        setNewEmail("");
        fetchApprovedEmails();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add email');
      }
    } catch (error) {
      toast.error('Failed to add email');
    } finally {
      setEmailsLoading(false);
    }
  };

  const handleRemoveEmail = async (email) => {
    if (!confirm(`Remove ${email} from approved list?`)) return;
    
    try {
      const response = await fetch(`${API}/admin/remove-approved-email`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        toast.success(`Removed ${email} from approved list`);
        fetchApprovedEmails();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove email');
      }
    } catch (error) {
      toast.error('Failed to remove email');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword.trim() || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResetLoading(true);
    try {
      const response = await fetch(`${API}/admin/reset-password/${resetUser.user_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: newPassword })
      });
      
      if (response.ok) {
        toast.success(`Password reset for ${resetUser.email}`);
        setShowPasswordReset(false);
        setResetUser(null);
        setNewPassword("");
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const openPasswordReset = (user) => {
    setResetUser(user);
    setNewPassword("");
    setShowPasswordReset(true);
  };

  const handleDeleteUser = async (user) => {
    if (!confirm(`Are you sure you want to delete ${user.name || user.email}?\n\nThis will permanently delete:\n- Their account\n- All their activities\n- All their badges\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/user/${user.user_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(`Deleted ${user.email}`);
        fetchUsers(); // Refresh the list
        if (selectedUser?.user_id === user.user_id) {
          setSelectedUser(null);
          setUserDetails(null);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleGenerateCode = async (user) => {
    setCodeUser(user);
    setGeneratedCode(null);
    setShowCodeGenerator(true);
    setCodeLoading(true);
    
    try {
      const response = await fetch(`${API}/admin/generate-login-code/${user.user_id}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedCode(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate code');
        setShowCodeGenerator(false);
      }
    } catch (error) {
      toast.error('Failed to generate code');
      setShowCodeGenerator(false);
    } finally {
      setCodeLoading(false);
    }
  };

  const handleGenerateCodeByEmail = async () => {
    if (!codeEmail.trim()) return;
    
    setCodeLoading(true);
    setGeneratedCode(null);
    
    try {
      const response = await fetch(`${API}/admin/generate-login-code-by-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: codeEmail.trim().toLowerCase() })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedCode(data);
        setCodeUser({ email: data.email, name: data.email.split('@')[0] });
        if (data.is_new_user) {
          toast.success('New user account created');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate code');
      }
    } catch (error) {
      toast.error('Failed to generate code');
    } finally {
      setCodeLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleGenerateDirectLink = async () => {
    setDirectLinkLoading(true);
    try {
      const response = await fetch(`${API}/admin/generate-direct-link`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const baseUrl = window.location.origin;
        const link = `${baseUrl}/access?token=${data.token}`;
        setDirectLink({ link, expiresIn: data.expires_in });
        setShowDirectLink(true);
      } else {
        toast.error('Failed to generate link');
      }
    } catch (error) {
      toast.error('Failed to generate link');
    } finally {
      setDirectLinkLoading(false);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    fetchUserDetails(user.user_id);
  };

  const handleDownloadPdf = async (userId, userName) => {
    try {
      const response = await fetch(`${API}/admin/user-pdf/${userId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `integritygo_report_${userName.replace(/\s+/g, '_').toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getProgressColor = (current, target) => {
    const percent = (current / target) * 100;
    if (percent >= 100) return "bg-emerald-500";
    if (percent >= 50) return "bg-yellow-500";
    return "bg-white/30";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-glow p-8 rounded-full">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-white/10 bg-card/50 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="IntegrityGO" className="h-8 w-auto" />
                <span className="font-semibold text-lg">Admin Dashboard</span>
              </div>
            </div>
            <Badge className="bg-purple-500/20 text-purple-400 border-0">
              Admin View
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Team Overview</h1>
            <p className="text-muted-foreground mt-1">
              Week of {weekStart} • {users.length} users
            </p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* Generate Direct Link Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateDirectLink}
              disabled={directLinkLoading}
              className="text-cyan-400 border-cyan-400/30 hover:bg-cyan-500/10"
              data-testid="generate-direct-link-btn"
            >
              <Link className="h-4 w-4 mr-2" />
              {directLinkLoading ? "..." : "Direct Link"}
            </Button>
            
            {/* Manage Users Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEmailManager(true)}
              className="text-purple-400 border-purple-400/30 hover:bg-purple-500/10"
              data-testid="manage-users-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Manage Access
            </Button>
            
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-background/50"
                data-testid="user-search"
              />
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/20">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{users.length}</p>
                  <p className="text-xs text-muted-foreground">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/20">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {users.reduce((sum, u) => sum + (u.total_inputs || 0), 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Inputs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-secondary/20">
                  <CalendarCheck className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-secondary">
                    {users.reduce((sum, u) => sum + (u.weekly_stats?.meetings || 0), 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Meetings Booked</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#FACC15]/20">
                  <Trophy className="h-5 w-5 text-[#FACC15]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {users.reduce((sum, u) => sum + (u.badges_earned || 0), 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Badges Earned</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card className="glass-panel border-white/10">
          <CardHeader>
            <CardTitle>All Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredUsers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {searchTerm ? "No users match your search." : "No users found."}
                </p>
              ) : (
                filteredUsers.map((u) => (
                  <div
                    key={u.user_id}
                    className="p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-all"
                    data-testid={`user-row-${u.user_id}`}
                  >
                    <div className="flex items-center gap-4">
                      {/* User Info */}
                      <div className="flex items-center gap-3 min-w-[200px]">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full" />
                          ) : (
                            <UserIcon className="h-5 w-5 text-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{u.name || "Unnamed"}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </div>

                      {/* Quick Stats */}
                      <div className="flex-1 hidden md:grid grid-cols-5 gap-2">
                        {CATEGORIES.map((cat) => {
                          const value = u.weekly_stats?.[cat.id] || 0;
                          const percent = Math.min((value / cat.target) * 100, 100);
                          return (
                            <div key={cat.id} className="text-center">
                              <div className="flex items-center justify-center gap-1 mb-1">
                                <cat.icon className={`h-3 w-3 ${cat.id === 'meetings' ? 'text-secondary' : 'text-primary'}`} />
                                <span className="text-xs font-medium">{value}/{cat.target}</span>
                              </div>
                              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all ${getProgressColor(value, cat.target)}`}
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Total & Badges */}
                      <div className="flex items-center gap-4">
                        <div className="text-center hidden sm:block">
                          <p className="text-lg font-bold text-primary">{u.total_inputs || 0}</p>
                          <p className="text-xs text-muted-foreground">inputs</p>
                        </div>
                        <div className="text-center hidden sm:block">
                          <p className="text-lg font-bold text-[#FACC15]">{u.badges_earned || 0}</p>
                          <p className="text-xs text-muted-foreground">badges</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewUser(u)}
                          className="text-primary border-primary/30 hover:bg-primary/10"
                          data-testid={`view-user-${u.user_id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadPdf(u.user_id, u.name || 'user')}
                          className="text-secondary border-secondary/30 hover:bg-secondary/10"
                          data-testid={`download-pdf-${u.user_id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openPasswordReset(u)}
                          className="text-muted-foreground hover:text-orange-400 hover:bg-orange-400/10"
                          title="Reset Password"
                          data-testid={`reset-password-${u.user_id}`}
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteUser(u)}
                          className="text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                          title="Delete User"
                          data-testid={`delete-user-${u.user_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* User Details Dialog */}
      <Dialog open={selectedUser !== null} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <DialogContent className="glass-panel border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedUser && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      {selectedUser.picture ? (
                        <img src={selectedUser.picture} alt={selectedUser.name} className="w-12 h-12 rounded-full" />
                      ) : (
                        <UserIcon className="h-6 w-6 text-primary" />
                      )}
                    </div>
                    <div>
                      <p className="text-xl">{selectedUser.name || "Unnamed"}</p>
                      <p className="text-sm text-muted-foreground font-normal">{selectedUser.email}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPdf(selectedUser.user_id, selectedUser.name || 'user')}
                    className="text-secondary border-secondary/30 hover:bg-secondary/10"
                    data-testid="download-pdf-dialog"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </DialogTitle>
              </DialogHeader>

              {detailsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : userDetails ? (
                <div className="space-y-6 py-4">
                  {/* Weekly Stats */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-primary" />
                      This Week's Progress
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      {CATEGORIES.map((cat) => {
                        const value = userDetails.stats?.[cat.id] || 0;
                        const percent = Math.min((value / cat.target) * 100, 100);
                        const isMeeting = cat.id === 'meetings';
                        return (
                          <div 
                            key={cat.id}
                            className={`p-3 rounded-xl border ${
                              percent >= 100 
                                ? isMeeting ? 'border-secondary/50 bg-secondary/10' : 'border-primary/50 bg-primary/10'
                                : 'border-white/10 bg-white/5'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <cat.icon className={`h-4 w-4 ${isMeeting ? 'text-secondary' : 'text-primary'}`} />
                              <span className="text-xs text-muted-foreground">{cat.label}</span>
                            </div>
                            <p className={`text-xl font-bold ${isMeeting ? 'text-secondary' : 'text-foreground'}`}>
                              {value}<span className="text-sm text-muted-foreground">/{cat.target}</span>
                            </p>
                            <Progress 
                              value={percent} 
                              className="h-1.5 mt-2 bg-white/10"
                              indicatorClassName={isMeeting ? "bg-secondary" : "bg-primary"}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Badges */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-[#FACC15]" />
                      Achievements ({userDetails.badges?.filter(b => b.earned_at).length || 0}/{userDetails.badges?.length || 0})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {userDetails.badges?.map((badge) => {
                        const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
                        const isEarned = !!badge.earned_at;
                        return (
                          <div
                            key={badge.badge_id}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                              isEarned 
                                ? 'border-[#FACC15]/30 bg-[#FACC15]/10' 
                                : 'border-white/10 bg-white/5 opacity-50'
                            }`}
                          >
                            <IconComponent className={`h-4 w-4 ${isEarned ? 'text-[#FACC15]' : 'text-muted-foreground'}`} />
                            <span className={`text-sm ${isEarned ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {badge.name}
                            </span>
                            {isEarned && <span className="text-[#FACC15]">✓</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Recent Activities */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-primary" />
                      Recent Activities
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {userDetails.activities?.slice(0, 10).map((activity) => {
                        const cat = CATEGORIES.find(c => c.id === activity.category);
                        const IconComponent = cat?.icon || PenTool;
                        const isMeeting = activity.category === 'meetings';
                        return (
                          <div
                            key={activity.activity_id}
                            className="flex items-center gap-3 p-2 rounded-lg bg-white/5"
                          >
                            <IconComponent className={`h-4 w-4 ${isMeeting ? 'text-secondary' : 'text-primary'}`} />
                            <span className="flex-1 text-sm">
                              +{activity.count} {cat?.label || activity.category}
                              {activity.note && <span className="text-muted-foreground ml-2">- {activity.note}</span>}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(activity.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                        );
                      })}
                      {(!userDetails.activities || userDetails.activities.length === 0) && (
                        <p className="text-center text-muted-foreground py-4">No activities yet</p>
                      )}
                    </div>
                  </div>

                  {/* Weekly History */}
                  {userDetails.history && userDetails.history.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-foreground mb-3">Weekly History</h3>
                      <div className="space-y-2">
                        {userDetails.history.slice(0, 4).map((week) => (
                          <div
                            key={week.week_start}
                            className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                          >
                            <span className="text-sm text-muted-foreground">
                              Week of {new Date(week.week_start + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                            <div className="flex items-center gap-4">
                              <span className="text-primary font-medium">{week.total_inputs} inputs</span>
                              <span className="text-secondary font-medium">{week.meetings} meetings</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">Failed to load user details</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Manager Dialog */}
      <Dialog open={showEmailManager} onOpenChange={setShowEmailManager}>
        <DialogContent className="glass-panel border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Mail className="h-5 w-5 text-purple-400" />
              Manage Approved Emails
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Add Email Form */}
            <form onSubmit={handleAddEmail} className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter email to approve..."
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="flex-1 bg-background/50"
                data-testid="new-email-input"
              />
              <Button 
                type="submit" 
                disabled={emailsLoading || !newEmail.trim()}
                className="bg-purple-500 hover:bg-purple-600"
                data-testid="add-email-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </form>
            
            <p className="text-xs text-muted-foreground">
              Only users with approved emails can register or log in to the app.
            </p>
            
            {/* Email List */}
            <div className="space-y-2">
              <h3 className="font-medium text-foreground flex items-center gap-2">
                Approved Emails ({approvedEmails.length})
              </h3>
              
              {approvedEmails.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No approved emails yet</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {approvedEmails.map((email) => {
                    const isRegistered = users.some(u => u.email?.toLowerCase() === email.toLowerCase());
                    return (
                      <div
                        key={email}
                        className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
                        data-testid={`email-row-${email}`}
                      >
                        <div className="flex items-center gap-3">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-foreground">{email}</span>
                          {isRegistered && (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-xs">
                              Registered
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-red-400 hover:bg-red-400/10"
                          onClick={() => handleRemoveEmail(email)}
                          data-testid={`remove-email-${email}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordReset} onOpenChange={setShowPasswordReset}>
        <DialogContent className="glass-panel border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Key className="h-5 w-5 text-orange-400" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          
          {resetUser && (
            <form onSubmit={handleResetPassword} className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <p className="text-sm text-muted-foreground">Resetting password for:</p>
                <p className="font-medium text-foreground">{resetUser.name || "Unnamed"}</p>
                <p className="text-sm text-muted-foreground">{resetUser.email}</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">New Temporary Password</label>
                <Input
                  type="text"
                  placeholder="Enter new password (min 6 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-background/50"
                  minLength={6}
                  required
                  data-testid="new-password-input"
                />
                <p className="text-xs text-muted-foreground">
                  Share this temporary password with the user. They should change it after logging in.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPasswordReset(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={resetLoading || newPassword.length < 6}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  data-testid="confirm-reset-btn"
                >
                  {resetLoading ? "Resetting..." : "Reset Password"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Login Code Generator Dialog */}
      <Dialog open={showCodeGenerator} onOpenChange={(open) => {
        setShowCodeGenerator(open);
        if (!open) {
          setGeneratedCode(null);
          setCodeUser(null);
          setCodeEmail("");
        }
      }}>
        <DialogContent className="glass-panel border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <KeyRound className="h-5 w-5 text-green-400" />
              Generate Login Code
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!codeUser && !generatedCode && (
              // Email input for generating code
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Generate a one-time login code for a user. Enter their email below:
                </p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="user@example.com"
                    value={codeEmail}
                    onChange={(e) => setCodeEmail(e.target.value)}
                    className="flex-1 bg-background/50"
                    data-testid="code-email-input"
                  />
                  <Button
                    onClick={handleGenerateCodeByEmail}
                    disabled={codeLoading || !codeEmail.trim()}
                    className="bg-green-500 hover:bg-green-600"
                  >
                    {codeLoading ? "..." : "Generate"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  If the email is approved but not registered, a new account will be created.
                </p>
              </div>
            )}
            
            {codeLoading && (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-green-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            
            {generatedCode && (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-sm text-muted-foreground">Login code for:</p>
                  <p className="font-medium text-foreground">{generatedCode.email}</p>
                </div>
                
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-xs text-green-400 mb-2">ONE-TIME LOGIN CODE</p>
                  <div className="flex items-center justify-between">
                    <span className="text-3xl font-mono font-bold text-green-400 tracking-widest">
                      {generatedCode.code}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyToClipboard(generatedCode.code)}
                      className="text-green-400 hover:bg-green-500/20"
                    >
                      <Copy className="h-5 w-5" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Expires in {generatedCode.expires_in}
                  </p>
                </div>
                
                <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
                  <p className="text-sm font-medium text-foreground">Send this to the user:</p>
                  <div className="p-2 bg-background/50 rounded text-sm text-muted-foreground">
                    <p>Your login code for IntegrityGO is: <strong className="text-foreground">{generatedCode.code}</strong></p>
                    <p className="mt-1">Go to the login page, click "Have a login code from admin?" and enter your email and this code.</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(`Your login code for IntegrityGO is: ${generatedCode.code}\n\nGo to the login page, click "Have a login code from admin?" and enter your email and this code.`)}
                    className="w-full"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Message
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Direct Access Link Dialog */}
      <Dialog open={showDirectLink} onOpenChange={setShowDirectLink}>
        <DialogContent className="glass-panel border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Link className="h-5 w-5 text-cyan-400" />
              Direct Access Link
            </DialogTitle>
          </DialogHeader>
          
          {directLink && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Share this link with users who have trouble logging in. They'll enter their approved email to access the dashboard.
              </p>
              
              <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                <p className="text-xs text-cyan-400 mb-2">DIRECT ACCESS LINK</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-foreground bg-background/50 p-2 rounded overflow-x-auto">
                    {directLink.link}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyToClipboard(directLink.link)}
                    className="text-cyan-400 hover:bg-cyan-500/20 shrink-0"
                  >
                    <Copy className="h-5 w-5" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Expires in {directLink.expiresIn}
                </p>
              </div>
              
              <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
                <p className="text-sm font-medium text-foreground">Send this to users:</p>
                <div className="p-2 bg-background/50 rounded text-sm text-muted-foreground">
                  <p>Click this link to access IntegrityGO 20x5:</p>
                  <p className="text-cyan-400 break-all">{directLink.link}</p>
                  <p className="mt-2">Enter your email address to log in.</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(`Click this link to access IntegrityGO 20x5:\n${directLink.link}\n\nEnter your email address to log in.`)}
                  className="w-full"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Message
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground text-center">
                Only users with approved emails can access the dashboard.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
